import { useState } from 'react';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/ui/accordion';
import type {
  SidebarSection,
  SidebarSubGroup,
  SidebarLeafItem,
} from '../../utils/groupData.util';
import styles from './Sidebar.module.scss';
import { MaterialIcon } from '@/ui/material-icon';
import { ResolvedIcon } from '../shared/ResolvedIcon';

// ─── Node type mapping ────────────────────────────────────────────────────────

function sectionToNodeType(sectionId: string): 'source' | 'integration' | 'transformation' | 'destination' {
  switch (sectionId) {
    case 'data-sources':
      return 'source';
    case 'integrations':
      return 'integration';
    case 'transformations':
      return 'transformation';
    case 'data-destinations':
      return 'destination';
    default:
      return 'source';
  }
}

// ─── Drag payload type ────────────────────────────────────────────────────────

export interface SidebarDragPayload {
  nodeType: 'source' | 'integration' | 'transformation' | 'destination';
  label: string;
  secondaryTitle: string;
  imageLink: string | null;
  /** API primary key: data_source_id or transformation id from catalog */
  id: string;
  sourceType?: string;
  destinationType?: string;
  /** Same as `id`, as string — stable API entity id for node data (not sidebar row key) */
  originalId: number;
}

export const SIDEBAR_DRAG_MIME = 'application/akashic-adp-sidebar';

// ─── Filter helper ────────────────────────────────────────────────────────────

function filterSections(
  items: SidebarSection[],
  query: string,
): SidebarSection[] {
  if (!query.trim()) return items;
  const q = query.toLowerCase();
  return items.reduce<SidebarSection[]>((acc, section) => {
    const labelMatch = section.label.toLowerCase().includes(q);
    const filteredSubGroups = section.subGroups
      .map((g) => ({
        ...g,
        items: g.items.filter((i) => i.label.toLowerCase().includes(q)),
      }))
      .filter((g) => g.items.length > 0);

    if (labelMatch || filteredSubGroups.length > 0) {
      acc.push({
        ...section,
        subGroups: labelMatch ? section.subGroups : filteredSubGroups,
      });
    }
    return acc;
  }, []);
}

// ─── Main component ───────────────────────────────────────────────────────────

interface SidebarContentProps {
  sections: SidebarSection[];
  searchQuery: string;
}

const SidebarContent = ({ sections, searchQuery }: SidebarContentProps) => {
  const [openParentItems, setOpenParentItems] = useState<string[]>([]);

  const visibleSections = filterSections(sections, searchQuery);

  return (
    <nav className={styles.content} aria-label="Main navigation">
      <Accordion
        type="multiple"
        value={openParentItems}
        onValueChange={setOpenParentItems}
        className={styles.accordion}
      >
        {visibleSections.map((section) => {
          const hasSubGroups = section.subGroups.length > 0;

          if (!hasSubGroups) {
            return (
              <div key={section.id} className={styles.menuButton}>
                <span className={styles.menuButtonLeft}>
                  <MaterialIcon name={section.icon} className={styles.menuIcon} />
                  <span className={styles.menuLabel}>{section.label}</span>
                </span>
              </div>
            );
          }

          return (
            <AccordionItem
              key={section.id}
              value={section.id}
              className={styles.accordionItem}
            >
              <AccordionTrigger className={styles.accordionTrigger}>
                <span className={styles.menuButtonLeft}>
                  <MaterialIcon name={section.icon} className={styles.menuIcon} />
                  <span className={styles.menuLabel}>{section.label}</span>
                </span>
                <MaterialIcon
                  name={openParentItems.includes(section.id) ? 'expand_more' : 'chevron_right'}
                  className={styles.menuChevron}
                />
              </AccordionTrigger>

              <AccordionContent className={styles.accordionContent}>
                {section.subGroups.map((subGroup) => (
                  <SubGroupRenderer key={subGroup.groupId} subGroup={subGroup} sectionId={section.id} />
                ))}
              </AccordionContent>
            </AccordionItem>
          );
        })}
      </Accordion>
    </nav>
  );
};

// ─── SubGroup renderer ────────────────────────────────────────────────────────

interface SubGroupRendererProps {
  subGroup: SidebarSubGroup;
  sectionId: string;
}

const SubGroupRenderer = ({ subGroup, sectionId }: SubGroupRendererProps) => {
  return (
    <div className={styles.subGroup}>
      {subGroup.groupLabel && (
        <p className={styles.subGroupLabel}>{subGroup.groupLabel}</p>
      )}
      <ul className={styles.leafList}>
        {subGroup.items.map((leaf) => (
          <li key={leaf.id}>
            <LeafItem leaf={leaf} sectionId={sectionId} />
          </li>
        ))}
      </ul>
    </div>
  );
};

// Leaf item (draggable)
interface LeafItemProps {
  leaf: SidebarLeafItem;
  sectionId: string;
}

const LeafItem = ({ leaf, sectionId }: LeafItemProps) => {
  const handleDragStart = (e: React.DragEvent<HTMLButtonElement>) => {
    const apiEntityId = leaf.dataSourceId ?? leaf.transformationId;
    if (apiEntityId == null) {
      return;
    }

    const payload: SidebarDragPayload = {
      nodeType: sectionToNodeType(sectionId),
      label: leaf.label,
      secondaryTitle: '',
      imageLink: leaf.imageLink,
      id: leaf.id,
      sourceType: leaf.sourceType,
      destinationType: leaf.destinationType,
      originalId: apiEntityId,
    };
    e.dataTransfer.setData(SIDEBAR_DRAG_MIME, JSON.stringify(payload));
    e.dataTransfer.effectAllowed = 'move';
  };

  return (
    <button
      type="button"
      className={styles.leafItem}
      draggable
      onDragStart={handleDragStart}
    >
      <MaterialIcon name='drag_indicator' />
      <ResolvedIcon imageLink={leaf.imageLink} alt={leaf.label} className={styles.leafIcon} />
      <span className={styles.leafLabel}>{leaf.label}</span>
    </button>
  );
};

export default SidebarContent;
